import { useEffect, useState, useCallback } from 'react'
import { SK } from '@/lib/storageKeys'

export type Theme = 'light' | 'dark' | 'system'

const STORAGE_KEY = SK.theme

function isTheme(v: string | null): v is Theme {
  return v === 'light' || v === 'dark' || v === 'system'
}

function getStoredTheme(): Theme {
  try {
    const v = localStorage.getItem(STORAGE_KEY)
    if (isTheme(v)) return v
  } catch {}
  return 'system'
}

function getEffectiveTheme(theme: Theme): 'light' | 'dark' {
  if (theme === 'light' || theme === 'dark') return theme
  if (typeof window !== 'undefined' && window.matchMedia('(prefers-color-scheme: dark)').matches) return 'dark'
  return 'light'
}

function applyTheme(theme: Theme): void {
  const eff = getEffectiveTheme(theme)
  const root = document.documentElement
  root.classList.remove('light', 'dark')
  root.classList.add(eff)
  root.style.colorScheme = eff
  try {
    localStorage.setItem(STORAGE_KEY, theme)
  } catch {}
}

export function useTheme() {
  const [theme, setThemeState] = useState<Theme>(() => getStoredTheme())

  const setTheme = useCallback((t: Theme) => {
    setThemeState(t)
    applyTheme(t)
  }, [])

  useEffect(() => {
    // sync on mount (early script already applied, but ensure state matches DOM)
    applyTheme(theme)

    // follow system when in 'system' mode
    const mql = window.matchMedia('(prefers-color-scheme: dark)')
    const onChange = () => {
      if (getStoredTheme() === 'system') {
        const eff = mql.matches ? 'dark' : 'light'
        const root = document.documentElement
        root.classList.remove('light', 'dark')
        root.classList.add(eff)
        root.style.colorScheme = eff
      }
    }
    // modern browsers
    if (typeof mql.addEventListener === 'function') mql.addEventListener('change', onChange)
    else mql.addListener(onChange as unknown as (ev: MediaQueryListEvent) => void)

    // sync across tabs
    const onStorage = (e: StorageEvent) => {
      if (e.key === STORAGE_KEY && isTheme(e.newValue)) {
        setThemeState(e.newValue)
        applyTheme(e.newValue)
      }
    }
    window.addEventListener('storage', onStorage)
    return () => {
      if (typeof mql.removeEventListener === 'function') mql.removeEventListener('change', onChange)
      else mql.removeListener(onChange as unknown as (ev: MediaQueryListEvent) => void)
      window.removeEventListener('storage', onStorage)
    }
  }, [theme])

  return { theme, setTheme }
}
